
import * as R from 'ramda';
import assert from 'assert';
import {
  takeLatest,
  select,
  spawn,
  delay,
  put,
  all,
  fork,
} from 'redux-saga/effects';
import {
  generativeAi as generativeAiApis,
  account as accountApis,
} from '../../../api.js';
import { apiSaga, apiSagaJob } from '../../../root-sagas.js';
import itinerarySagas from './itinerary-sagas.js';
import revenueInsightsSagas from './revenueInsights-sagas.js';
import aiRulesSagas from './ai-rules-sagas.js';
import partnerSettingsSagas from './partner-settings/sagas.js';
import statusCheckSagas from './status-check-sagas.js';
import downloadXLSXSagas from '../ItineraryAssist/downloads/dowloads-sagas.js';
import gondolaSagas from '../Settings/Gondola/__store__/gondola-sagas.js';
import integrationSettingsSagas from './integration-settings-sagas.js';
import learningSagas from './learning-sagas';

import { fetchAsyncJob as fetchAsyncJobImport } from '../../../utils/fetchAsyncJob.js';

function* onLocationChange({ payload }) {
  if (R.path(['location', 'pathname'], payload).includes('generative-ai/configure-ai')
  && !R.path(['location', 'pathname'], payload).includes('generative-ai-sign-up')
  ) {
    yield all([
      bgRefreshAssets(),
      getAllCategories(),
    ]);
  }
}

export function* getAllAssets() {
  yield apiSaga('generativeAI_getAllAssets', generativeAiApis.getAllAssets, [{ contentType: 'vectorstore' }]);
}
function* pollingAssetsTask () {
  let shouldStop = false;
  while (!shouldStop) {
    yield apiSaga('generativeAI_bgRefreshAssets', generativeAiApis.getAllAssets, [{ contentType: 'vectorstore', skipLoader: true }]);
    const assets = yield select(state => state.generativeAi.configuration.assets);
    if (assets.some(a => a.status !== 'vectorized' || (a.status || '').includes('error'))) {
      yield delay(2000);
    } else shouldStop = true;
  }
  return true;
}
function* bgRefreshAssets() {
  // const bgTask = yield fork(pollingAssetsTask);
  // yield delay(300e3);
  // yield cancel(bgTask);

  yield fork(pollingAssetsTask);
}

function* onCreateProcessFileJobSuccess({ payload }) {
  if (payload.jobs && payload.jobs.length) {
    yield all([
      bgRefreshAssets(),
      spawn(fetchAsyncJobImport('generativeAI_fileProcessJob', generativeAiApis.checkVectorJobStatus), { jobs: payload.jobs }),
    ]);
  }
}

function* getAllCategories() {
  yield apiSaga('generativeAI_getAllCategories', generativeAiApis.getAllCategories, []);
}

export function* sendPrompt({ type }) {
  const {
    playgroundPrompt,
    scopes,
    responseStyles,
    emailBody,
  } = yield select(state => state.generativeAi.configuration);
  const apiPayload = {
    emailId: 'playground',
    prompt: playgroundPrompt,
    scopes,
    responseStyles: emailBody ? responseStyles : {},
    emailBody,
    skipLoader: true,
  }
  const retVal = yield apiSaga(type, generativeAiApis.sendPrompt, [apiPayload])
  if (retVal) {
    const { jobs } = retVal;
    if (jobs && jobs.length > 0) {
      yield spawn(fetchAsyncJobImport(type, generativeAiApis.checkVectorJobStatus), { jobs, mailId: 'playground' });
    }
  }
}

function* saveResponseStyles({ type, payload }) {
  yield apiSaga(type, accountApis.updateUser, [{ responseStyles: payload }])
}

export function* uploadAsset({ type }) {
  const { addEditAssetDialog } = yield select(state => state.generativeAi.configuration);
  yield apiSaga(
    type,
    addEditAssetDialog.path ? generativeAiApis.updateAsset : generativeAiApis.uploadAsset,
    [addEditAssetDialog]
  )
}



export function* deleteAsset({ type, payload }) {
  yield apiSaga(type, generativeAiApis.deleteAsset, [payload])
}

export function* doConfigureAI({ type }) {
  const retVal = yield apiSaga(
    type,
    generativeAiApis.vectorProcess,
    [{ skipLoader: true }],
  );
  let jobs = [];
  if (retVal && retVal.jobs) {
    jobs = retVal.jobs;
  }
  if (jobs && jobs.length) {
    yield spawn(fetchAsyncJobImport(type, generativeAiApis.checkVectorJobStatus), { jobs });
  }
}

export function* markAiReady() {
  yield apiSaga('generativeAI_doConfigureAI_markAiReady', generativeAiApis.saveAISettings, [{
    generativeAIReady: true,
    generativeAIReadyAt: new Date().toISOString(),
  }]);
  yield delay(8000);
  yield put({ type: 'generativeAI_dismissAiReadyMessage' })
}

function* onConfigureAISuccess({ payload }) {
  if (payload.every(o => o.result && o.result.result === 'success')) {
    yield markAiReady();
  }
}

function* onFileProcessJobSuccess({ payload }) {
  if (payload.every(o => o.status === 'success')) {
    yield markAiReady();
  }
}


function* apiKeysloadAllKeys() {
  yield apiSaga('genAi_apiKeys_load', generativeAiApis.apiKeysloadAllKeys, []);
}

export function* apiKeysGenerate() {
  yield apiSaga('genAi_apiKeys_generate', generativeAiApis.apiKeysGenerate, []);
}

export function* apiKeysGenerateClear() {
  yield delay(30000);
  yield put({ type: 'genAi_apiKeys_generate_CLEAR' });
}

export function* apiKeysRemove({ id }) {
  yield apiSaga('genAi_apiKeys_remove', generativeAiApis.apiKeysRemove, [id]);
}

function* triggerImportJob({ payload }) {
  // get the current integration  and partnerId from the state
  const { app: integration, hint: partnerId } = yield select(
    R.pathOr({}, ['generativeAi', 'editAppKey']),
  );
  assert(integration, 'integration is required');
  assert(partnerId, 'partnerId is required');
  const newPayload = {
    ...payload,
    integration,
    partnerId,
  };
  // create the job
  yield apiSagaJob('genAI_ImportFilters', generativeAiApis.importFilters, [newPayload]);
}


/*
Here is the flow
  upload/delete asset -> create job to process file -> polling files & checking job status
  -> stop polling when all files are processed and vectorized
  -> if a single file processing job is succes, mark ai ready
 */
// eslint-disable-next-line import/no-anonymous-default-export
export default [
  takeLatest('@@router/LOCATION_CHANGE', onLocationChange),
  takeLatest('generativeAI_getAllAssets', getAllAssets),
  takeLatest('genAi_apiKeys_load', apiKeysloadAllKeys),
  takeLatest('getAi_apiKeys_generate', apiKeysGenerate),
  takeLatest('genAi_apiKeys_generate_SUCCESS', apiKeysloadAllKeys), // reload keys after new one is generated
  takeLatest('genAi_apiKeys_generate_SUCCESS', apiKeysGenerateClear), // reload keys after new one is generated
  takeLatest('genAi_apiKeys_remove', apiKeysRemove),
  takeLatest('genAi_apiKeys_remove_SUCCESS', apiKeysloadAllKeys), // reload keys after one is removed
  takeLatest([
    'generativeAI_uploadAsset_SUCCESS',
    'generativeAI_deleteAsset_SUCCESS',
  ], doConfigureAI),
  takeLatest('generativeAi_processFile_SUCCESS', onCreateProcessFileJobSuccess),
  takeLatest('generativeAI_sendPrompt', sendPrompt),
  takeLatest('generativeAI_playgroundResponseStyles', saveResponseStyles),
  takeLatest('generativeAI_uploadAsset', uploadAsset),
  takeLatest('generativeAI_deleteAsset', deleteAsset),
  takeLatest('generativeAI_doConfigureAI', doConfigureAI),
  takeLatest('generativeAI_doConfigureAI_JobCheckComplete', onConfigureAISuccess),
  takeLatest('generativeAI_fileProcessJob_JobCheckComplete', onFileProcessJobSuccess),
  takeLatest('genAI_startImportFilters', triggerImportJob),
  ...itinerarySagas,
  ...revenueInsightsSagas,
  ...aiRulesSagas,
  ...partnerSettingsSagas,
  ...statusCheckSagas,
  ...downloadXLSXSagas,
  ...gondolaSagas,
  ...integrationSettingsSagas,
  ...learningSagas,
];
